import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import SideNavList from './SideNavList';
import Accordion from '../modules/Accordion';
import { GET_NAV } from '../../queries';
import { WidthContext } from '../../contexts/WidthContextProvider';

const SideNav = ({ isMobileMenuOpen, handleMobileMenu }) => {
  const { isMobile } = useContext(WidthContext);
  const location = useLocation();
  const { data, loading } = useQuery(GET_NAV);
  const [activeItem, setActiveItem] = useState('');
  const [slug, setSlug] = useState('');

  useEffect(() => {
    if (location.pathname.length > 1) {
      const paths = location.pathname.split('/');
      const path = paths[3];
      setActiveItem(path);
      setSlug(path);
    }

    handleMobileMenu();
  }, [location.pathname]);

  let categories;
  if (loading) {
    categories = [];
  } else {
    categories = data
      ? data.sideNav.data.attributes.categoryContainer.map(
          (cont) => cont.categories,
        )
      : [];
  }

  const handleSelect = (id) => {
    setActiveItem(id);
  };

  const toolsNav = [
    {
      id: 100,
      name: 'Mastering Toolbox',
      slug: 'mastering-toolbox',
      documents: [
        {
          data: {
            id: 110,
            attributes: {
              name: 'IMF CPL Parser',
              slug: 'cpl-parser',
            },
          },
        },
        {
          data: {
            id: 111,
            attributes: {
              name: 'Naming Spec Decoder Ring',
              slug: 'naming-spec-decoder-ring',
            },
          },
        },
        {
          data: {
            id: 112,
            attributes: {
              name: 'ST2086 Extractor',
              slug: 'st2086-extractor',
            },
          },
        },
        {
          data: {
            id: 113,
            attributes: {
              name: 'Dolby Vision XML Checker',
              slug: 'dovi-checker',
            },
          },
        },
        {
          data: {
            id: 114,
            attributes: {
              name: 'Naming Convention Lookup',
              slug: 'naming-convention-lookup',
            },
          },
        },
        {
          data: {
            id: 115,
            attributes: {
              name: 'Language Table Decoder Ring',
              slug: 'language-decoder-ring',
            },
          },
        },
        {
          data: {
            id: 116,
            attributes: {
              name: 'Digital Cinema Naming Decoder',
              slug: 'digital-cinema-naming-decoder',
            },
          },
        },
        {
          data: {
            id: 117,
            attributes: {
              name: 'Safe Area Calculator',
              slug: 'safe-area-calculator',
            },
          },
        },
      ],
    },
  ];

  const calculatorNav = [
    {
      id: 1,
      name: 'Aspect Ratio Calculators',
      documents: [
        {
          data: {
            id: 2,
            attributes: {
              name: 'Active Line',
              slug: 'active-line',
            },
          },
        },
        {
          data: {
            id: 3,
            attributes: {
              name: 'Active Area vs Aspect Ratio',
              slug: 'active-area-vs-aspect-ratio',
            },
          },
        },
        {
          data: {
            id: 4,
            attributes: {
              name: 'Active Area',
              slug: 'active-area',
            },
          },
        },
        {
          data: {
            id: 5,
            attributes: {
              name: 'Pillarboxing',
              slug: 'pillarboxing',
            },
          },
        },
      ],
      slug: 'aspect',
    },
    {
      id: 6,
      name: 'Frame Size Calculators',
      documents: [
        {
          data: {
            id: 5,
            attributes: {
              name: 'Uncompressed Frame Size',
              slug: 'uncompressed-frame-size',
            },
          },
        },
      ],
      slug: 'frame-size',
    },
    {
      id: 7,
      name: 'Luminance Calculator',
      documents: [
        {
          data: {
            id: 7,
            attributes: {
              name: 'Nits and Foot-Lamberts',
              slug: 'nits-foot-lamberts',
            },
          },
        },
      ],
      slug: 'luminance',
    },
  ];

  return (
    <div className={`SideNav ${isMobileMenuOpen ? 'open' : ''} `}>
      <div>
        {categories.map((category) => {
          return (
            <div
              key={category.data.attributes.slug}
              className="SideNav__Category">
              <Accordion
                slug={category.data.attributes.slug}
                title={category.data.attributes.name}>
                <SideNavList
                  className="SideNav__List"
                  items={category.data.attributes.subcategoryContainer
                    .filter((container) => container.subcategory)
                    .map((container) => ({
                      value: container.subcategory.data.id,
                      name: container.subcategory.data.attributes.name,
                      documents:
                        container.subcategory.data.attributes.documentContainer.map(
                          (container) => container.document,
                        ),
                      slug: container.subcategory.data.attributes.slug,
                    }))}
                  activeItem={activeItem}
                  onSelect={handleSelect}
                  slug={slug}
                  categorySlug={category.data.attributes.slug}
                />
              </Accordion>
            </div>
          );
        })}
        <div className="SideNav__Category">
          <Accordion slug="calculators" title="Calculators">
            <SideNavList
              className="SideNav__List"
              items={calculatorNav}
              activeItem={activeItem}
              onSelect={handleSelect}
              slug={slug}
              categorySlug="calculators"
            />
          </Accordion>
        </div>
        <div className="SideNav__Category">
          <Accordion slug="tools" title="Tools">
            <SideNavList
              className="SideNav__List"
              items={toolsNav}
              activeItem={activeItem}
              onSelect={handleSelect}
              slug={slug}
              categorySlug="tools"
            />
          </Accordion>
        </div>
        {isMobile && (
          <>
            <div className="SideNav__Category">
              <Accordion slug="glossary" title="Glossary" />
            </div>
            <div className="SideNav__Category">
              <Accordion slug="contact" title="Contact" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

SideNav.propTypes = {
  isMobileMenuOpen: PropTypes.bool,
  handleMobileMenu: PropTypes.func,
};

export default SideNav;
